/**
 * Render costs of an Echo (ECL, plans/2026-10-20-echo-cost-ledger-elevenlabs-
 * remakes-implementation-plan.md §6.7 and §6.11; contracts C3 to C5 in
 * plans/evidence/2026-10-20-ecl/contracts.md).
 *
 * A render is one pipeline run that produced a video for an Echo. The first
 * render is the Echo itself; every later one (a CRM remake, an operator
 * re-cut, a customer edit, a Studio re-cut, a redelivery) is a derivative
 * render with its own ledger, rolled up onto the parent as one RENDER# row.
 * Each cost row lives in exactly one ledger, so nothing here double counts.
 *
 * Pure helpers shared by the render costs card, the remakes card and the
 * child Echo banner. None of them ever turns a missing ledger into $0.
 */
import type {
  EchoflicksSessionRemake,
  EchoflicksSessionRenderCost,
  EchoflicksVideoSession,
} from "../types";
import { costDomainLabel, formatCostUsd } from "./EchoCostSection";

export { RENDER_KIND_LABELS, renderKindLabel } from "./renderKinds";

/** True when the Echo has at least one derivative render (C3 rollup). */
export function hasDerivativeRenders(
  session: Pick<EchoflicksVideoSession, "echo_cost_derivatives_count">,
): boolean {
  const count = Number(session.echo_cost_derivatives_count);
  return Number.isFinite(count) && count > 0;
}

function finite(value: unknown): number | null {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * The cost cell of a render row. "Not recorded" when the render has no
 * ledger (a remake requested before ECL, or a ledger with no rows yet);
 * "Unpriced" when it has rows but none of them carries a price.
 */
export function renderCostLabel(
  row: Pick<
    EchoflicksSessionRenderCost,
    "cost_recorded" | "total_usd" | "line_item_count" | "unknown_count"
  >,
): string {
  if (row.cost_recorded === false) return "Not recorded";
  const total = finite(row.total_usd);
  const lines = finite(row.line_item_count);
  const unknown = finite(row.unknown_count) ?? 0;
  if (total == null) {
    return (lines ?? 0) > 0 || unknown > 0 ? "Unpriced" : "Not recorded";
  }
  if (total === 0 && lines === 0) return "Not recorded";
  return formatCostUsd(total);
}

/**
 * The render's total including its own derivative renders (an edit of an
 * edit), when that differs from its own total; else null.
 */
export function renderDescendantsTotal(
  row: Pick<
    EchoflicksSessionRenderCost,
    "total_usd" | "descendants_total_usd" | "cost_recorded"
  >,
): number | null {
  if (row.cost_recorded === false) return null;
  const own = finite(row.total_usd);
  const all = finite(row.descendants_total_usd);
  if (all == null || own == null) return null;
  return Math.abs(all - own) >= 0.005 ? all : null;
}

/** The render's biggest cost domains, most spend first. */
export function topDomains(
  byDomain: Record<string, number | null> | null | undefined,
  limit = 2,
): string[] {
  return Object.entries(byDomain ?? {})
    .map(([domain, value]) => [domain, finite(value) ?? 0] as const)
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([domain]) => costDomainLabel(domain));
}

/**
 * The cost badge of one remake on the Remakes card, from the C5 remake
 * fields. Null when the API sent none of them (it predates ECL): the card then
 * shows no badge, exactly as before.
 */
export function remakeCostLabel(
  remake: Pick<
    EchoflicksSessionRemake,
    | "cost_recorded"
    | "cost_total_usd"
    | "cost_line_item_count"
    | "cost_unknown_count"
  >,
): string | null {
  const recorded = remake.cost_recorded;
  const total = finite(remake.cost_total_usd);
  if (recorded == null && total == null) return null;
  if (recorded === false) return "Not recorded";
  const lines = finite(remake.cost_line_item_count);
  const unknown = Math.max(0, finite(remake.cost_unknown_count) ?? 0);
  if (total == null) return unknown > 0 ? "Unpriced" : "Not recorded";
  if (total === 0 && lines === 0) return "Not recorded";
  return unknown > 0
    ? `${formatCostUsd(total)} + ${unknown} unpriced`
    : formatCostUsd(total);
}

/** The Echo page of a session; the session id is its workload id. */
export function sessionShowPath(sessionId: string): string {
  return `/echoflicks_video_sessions/${encodeURIComponent(sessionId)}/show`;
}

/** The parent of a derivative render, or null for a first render. */
export function derivativeParentId(
  session: Pick<
    EchoflicksVideoSession,
    "is_derivative" | "parent_session_id" | "parent_workload_id"
  >,
): string | null {
  if (session.is_derivative !== true) return null;
  return session.parent_session_id || session.parent_workload_id || null;
}
